/**
 * Analytics consent state for the event emitter (RFC-0974 §6.3, §13).
 *
 * A tiny module-level boolean the ConsentProvider keeps in sync (via
 * `consent/analyticsGate.ts`) on every consent change. The emitter re-reads it
 * immediately before each send, and a withdrawal (permitted true→false) triggers
 * a drain of the queued events. Default-deny until consent is explicitly granted.
 *
 * The withdraw handler is registered by the emitter (one-directional import — no
 * cycle: this module imports nothing from `eventsClient`).
 */

import { createNadDebug } from '../utils/debugLog'

const log = createNadDebug('dw:analytics-events:consent-gate')

let permitted = false
let onWithdraw: (() => void) | null = null
let onGrant: (() => void) | null = null

/** The emitter registers its queue-drain here (called on withdrawal). */
export function registerWithdrawHandler(fn: () => void): void {
  onWithdraw = fn
}

/**
 * Register a handler invoked when analytics consent transitions false→true.
 * Used to (re)fire once-per-session signals (e.g. `app_session_started`) that
 * were suppressed while consent was still pending. Single handler (last wins).
 */
export function registerGrantHandler(fn: () => void): void {
  onGrant = fn
}

/** Whether analytics event emission is currently permitted. */
export function isAnalyticsAllowed(): boolean {
  return permitted
}

/**
 * Update the analytics-consent decision. On withdrawal (true→false) the registered
 * drain handler runs so queued events are dropped and pending retries suppressed.
 */
export function setAnalyticsConsent(next: boolean): void {
  const was = permitted
  permitted = next
  if (was === next) return
  log({ event: was && !next ? 'withdrawn' : 'granted', permitted: next })
  if (was && !next && onWithdraw) {
    onWithdraw()
  }
  if (!was && next && onGrant) {
    onGrant()
  }
}

/** Test helper — reset to default-deny. */
export function _resetConsentForTest(): void {
  permitted = false
  // Clear the grant handler so a handler registered in one test can't leak into
  // a later test that grants consent. NOTE: `onWithdraw` is intentionally NOT
  // cleared — the emitter registers it exactly once at module import
  // (`registerWithdrawHandler(severSession)`), so clearing it here would strand
  // withdrawal-drain for the rest of a test file.
  onGrant = null
}
